import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, ChevronDown } from "lucide-react";
import usePageMeta from "../hooks/usePageMeta";
import StarfieldCanvas from "../components/StarfieldCanvas";
import StargatePortal from "../components/StargatePortal";
import EditorialRibbon from "../components/EditorialRibbon";
import MaskedLines from "../components/MaskedLines";
import Reveal from "../components/Reveal";
import GuidebookCard from "../components/GuidebookCard";
import {
  SHOP_URL,
  IMAGES,
  MANIFESTO,
  GUIDEBOOKS,
  RIBBON_ITEMS,
} from "../data/content";

const heroLines = ["Perceive beyond", "the coordinate horizon.", "Awaken non-local vision."];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0.15]);

  return (
    <section
      ref={ref}
      data-testid="home-hero"
      className="relative flex min-h-[100svh] items-center overflow-hidden"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0" aria-hidden="true">
        <div className="h-full w-full bg-[radial-gradient(circle_at_70%_35%,rgba(139,92,246,0.28),transparent_30%),radial-gradient(circle_at_35%_65%,rgba(56,189,248,0.18),transparent_28%),linear-gradient(135deg,#07080B_0%,#0B1020_45%,#120B24_100%)]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#07080B] via-[#07080B]/70 to-[#07080B]/25" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#07080B] via-transparent to-[#07080B]/70" />
      </motion.div>
      <StarfieldCanvas className="absolute inset-0" />
      <StargatePortal
        size={620}
        className="absolute -right-52 top-1/2 hidden -translate-y-1/2 opacity-40 md:block"
      />

      <motion.div
        style={{ opacity: fade }}
        className="relative mx-auto w-full max-w-7xl px-5 pb-28 pt-32 sm:px-8 lg:px-10"
      >
        <p className="eyebrow flex items-center gap-3">
          <span className="h-1.5 w-1.5 rounded-full bg-[#38BDF8] animate-pulse-amber" />
          Consciousness Ascending // Sensory Protocols
        </p>
        <h1 className="font-display mt-8 font-light leading-[1.05] tracking-tight text-[clamp(2.1rem,7.5vw,5.25rem)]">
          <MaskedLines lines={heroLines} delay={0.15} />
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="body-muted mt-8 max-w-xl text-base sm:text-lg"
        >
          Rigorous, step-by-step guidebooks and field methodologies for Remote
          Viewing and Out-of-Body Consciousness. Sovereign discernment meets
          cosmic navigation.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <Link
            to="/guidebooks"
            data-testid="hero-explore-guidebooks-btn"
            className="inline-flex items-center gap-3 border border-[#8B5CF6] bg-[#8B5CF6]/15 px-7 py-4 font-mono-ui text-[11px] uppercase tracking-[0.22em] text-[#F0F2F8] transition-all duration-300 hover:bg-[#8B5CF6]/30 hover:shadow-[0_0_28px_rgba(139,92,246,0.4)]"
          >
            Explore Guidebooks <ArrowRight className="h-3.5 w-3.5 text-[#A78BFA]" />
          </Link>
          <a
            href={SHOP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-fourthwall-shop-btn"
            className="inline-flex items-center gap-3 border border-white/20 px-7 py-4 font-mono-ui text-[11px] uppercase tracking-[0.22em] text-[#F0F2F8] transition-all duration-300 hover:border-[#38BDF8]/70 hover:shadow-[0_0_24px_rgba(56,189,248,0.25)]"
          >
            Visit Fourthwall Shop <ArrowUpRight className="h-3.5 w-3.5 text-[#7DD3FC]" />
          </a>
          <Link
            to="/remote-viewing"
            data-testid="hero-methodology-btn"
            className="group inline-flex items-center gap-2 px-2 py-4 font-mono-ui text-[11px] uppercase tracking-[0.22em] text-[#8E95A5] transition-colors duration-300 hover:text-[#A78BFA]"
          >
            The Methodologies
            <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1.5" />
          </Link>
        </motion.div>
      </motion.div>

      <div className="absolute inset-x-0 bottom-0 border-t border-white/[0.07] bg-[#07080B]/50 backdrop-blur-sm">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-6 px-5 py-4 sm:px-8 lg:px-10">
          <p className="coordinate-badge text-[10px] sm:text-[11px]">
            LAT 37.42° N // LON 122.17° W — PROTOCOL: CRV STAGE I–VI // GATEWAY H-SYNC
          </p>
          <ChevronDown className="h-4 w-4 shrink-0 animate-bounce text-[#5F6677]" aria-hidden="true" />
        </div>
      </div>
    </section>
  );
}

function Manifesto() {
  return (
    <section className="relative mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:px-10 lg:py-36" data-testid="manifesto-section">
      <Reveal>
        <p className="eyebrow">The Premise</p>
      </Reveal>
      <Reveal delay={0.1}>
        <h2 className="font-display mt-6 max-w-3xl text-3xl font-light leading-[1.12] tracking-tight sm:text-4xl lg:text-5xl">
          Three chapters of a single discipline.
        </h2>
      </Reveal>
      <div className="mt-16 grid gap-px border border-white/[0.07] bg-white/[0.07] lg:grid-cols-3">
        {MANIFESTO.map((m, i) => (
          <Reveal
            key={m.chapter}
            delay={0.12 * i}
            className="group bg-[#0A0C12] p-8 transition-colors duration-500 hover:bg-[#0F121B] lg:p-10"
          >
            <p className="font-mono-ui text-sm tracking-[0.3em] text-[#38BDF8]">
              {m.chapter}
            </p>
            <h3 className="font-display mt-8 text-2xl leading-snug text-[#F0F2F8]">
              {m.title}
            </h3>
            <p className="body-muted mt-5 text-sm leading-relaxed">{m.body}</p>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function PillarCard({
  testid,
  image,
  imgAlt,
  eyebrow,
  title,
  subtitle,
  desc,
  href,
  cta,
  className = "",
}) {
  return (
    <Reveal className={className}>
      <Link to={href} data-testid={testid} className="group block">
        <div className="relative overflow-hidden border border-white/10 transition-all duration-500 group-hover:border-[#8B5CF6]/40 group-hover:shadow-[0_0_60px_rgba(139,92,246,0.18)]">
          <div
            className="aspect-[4/5] w-full bg-[radial-gradient(circle_at_50%_38%,rgba(139,92,246,0.28),transparent_28%),radial-gradient(circle_at_55%_55%,rgba(56,189,248,0.12),transparent_40%),linear-gradient(160deg,#090B12_0%,#101424_52%,#090B12_100%)] transition-transform duration-[1400ms] ease-out group-hover:scale-[1.04]"
            role="img"
            aria-label={imgAlt}
          />
          <div
            className="absolute inset-0 bg-gradient-to-t from-[#07080B] via-[#07080B]/25 to-transparent"
            aria-hidden="true"
          />
          <div className="absolute inset-x-0 bottom-0 p-7 sm:p-9">
            <p className="text-[10px] font-mono-ui uppercase tracking-[0.24em] text-[#38BDF8]">
              {eyebrow}
            </p>
            <h3 className="font-display mt-3 text-3xl text-[#F0F2F8] sm:text-4xl">
              {title}
            </h3>
            <p className="mt-3 font-mono-ui text-[11px] uppercase tracking-[0.2em] text-[#A78BFA]">
              {subtitle}
            </p>
          </div>
        </div>
        <p className="body-muted mt-6 max-w-md text-sm leading-relaxed">{desc}</p>
        <span className="mt-5 inline-flex items-center gap-2 font-mono-ui text-[11px] uppercase tracking-[0.22em] text-[#A78BFA]">
          {cta}
          <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1.5" />
        </span>
      </Link>
    </Reveal>
  );
}

function Pillars() {
  return (
    <section className="relative mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:px-10 lg:py-32">
      <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
        <div>
          <Reveal>
            <p className="eyebrow">The Two Pillars</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display mt-6 max-w-xl text-3xl font-light leading-[1.12] tracking-tight sm:text-4xl lg:text-5xl">
              Choose your first door.
            </h2>
          </Reveal>
        </div>
        <Reveal delay={0.2}>
          <p className="body-muted max-w-sm text-sm leading-relaxed">
            Two disciplines, one conviction: perception extends further than the
            body. Start with either — the training compounds.
          </p>
        </Reveal>
      </div>
      <div className="mt-16 grid gap-12 lg:grid-cols-2 lg:gap-14">
        <PillarCard
          testid="pillar-card-remote-viewing"
          image={IMAGES.rv}
          imgAlt="Field Manual // Coordinate discipline"
          eyebrow="Discipline 01"
          title="Remote Viewing"
          subtitle="Observational & target-directed"
          desc="Scientific coordinate-driven sessions where you log sensory impressions without leaving biological consciousness. Verifiable, structured, quietly profound."
          href="/remote-viewing"
          cta="Enter the discipline"
        />
        <PillarCard
          testid="pillar-card-astral-projection"
          image={IMAGES.ap}
          imgAlt="Field Manual // Beyond-body transit"
          eyebrow="Discipline 02"
          title="Astral Projection"
          subtitle="Experiential & full-body relocation"
          desc="Conscious separation of the subtle vehicle into higher-frequency planes of non-physical reality. The most direct encounter with the nature of awareness."
          href="/astral-projection"
          cta="Enter the discipline"
          className="lg:mt-24"
        />
      </div>
    </section>
  );
}

function FeaturedGuidebooks() {
  const featured = GUIDEBOOKS.slice(0, 3);
  return (
    <section className="relative border-t border-white/[0.06] bg-[#0C0E14]/40 py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-10">
        <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
          <div>
            <Reveal>
              <p className="eyebrow">Featured Pathways</p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-display mt-6 max-w-2xl text-3xl font-light leading-[1.12] tracking-tight sm:text-4xl lg:text-5xl">
                Field manuals for the serious beginner.
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <Link
              to="/guidebooks"
              data-testid="home-view-all-guidebooks-btn"
              className="group inline-flex items-center gap-2 font-mono-ui text-[11px] uppercase tracking-[0.22em] text-[#A78BFA]"
            >
              View all guidebooks
              <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1.5" />
            </Link>
          </Reveal>
        </div>
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {featured.map((book, i) => (
            <Reveal key={book.id} delay={0.1 * i} className="h-full">
              <GuidebookCard book={book} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Newcomer() {
  const [path, setPath] = useState(null);
  const rec =
    path === "rv" ? GUIDEBOOKS[0] : path === "ap" ? GUIDEBOOKS[1] : null;

  return (
    <section className="relative mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:px-10 lg:py-32" data-testid="newcomer-section">
      <div className="grid gap-14 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="eyebrow">For the Newcomer</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display mt-6 text-3xl font-light leading-[1.12] tracking-tight sm:text-4xl lg:text-5xl">
              Begin where you stand.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="body-muted mt-6 max-w-md text-sm leading-relaxed sm:text-base">
              No initiation, no prior ability required — only a quiet room, a
              pen, and the willingness to record what arrives before judging it.
              Choose the doorway that matches your temperament:
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-10 space-y-5">
              {[
                { n: "01", t: "Define the intent", d: "Observe a hidden target, or explore beyond the body." },
                { n: "02", t: "Choose the doorway", d: "Analytical structure (RV) or experiential transit (AP)." },
                { n: "03", t: "Train with structure", d: "Daily drills, session logs, and honest verification." },
              ].map((s) => (
                <div key={s.n} className="flex gap-5 border-b border-white/[0.06] pb-5">
                  <span className="font-mono-ui text-[11px] tracking-[0.2em] text-[#38BDF8]">
                    {s.n}
                  </span>
                  <div>
                    <p className="text-sm text-[#F0F2F8]">{s.t}</p>
                    <p className="mt-1 text-sm text-[#5F6677]">{s.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.25} className="lg:col-span-7">
          <div className="relative flex h-full flex-col border border-white/[0.08] bg-[#0F121B]/70 p-8 sm:p-10">
            <div
              className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#8B5CF6]/70 to-transparent"
              aria-hidden="true"
            />
            <p className="coordinate-badge">Orientation Protocol // Select a Doorway</p>
            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              <button
                data-testid="newcomer-choice-rv-btn"
                onClick={() => setPath("rv")}
                className={`border p-6 text-left transition-all duration-300 ${
                  path === "rv"
                    ? "border-[#8B5CF6] bg-[#8B5CF6]/15 shadow-[0_0_24px_rgba(139,92,246,0.25)]"
                    : "border-white/10 hover:border-[#8B5CF6]/50"
                }`}
              >
                <p className="font-display text-xl text-[#F0F2F8]">
                  I want evidence
                </p>
                <p className="mt-2 text-sm text-[#8E95A5]">
                  Coordinate targets, pen and paper, verifiable feedback.
                </p>
                <p className="mt-4 font-mono-ui text-[10px] uppercase tracking-[0.2em] text-[#38BDF8]">
                  Remote Viewing
                </p>
              </button>
              <button
                data-testid="newcomer-choice-ap-btn"
                onClick={() => setPath("ap")}
                className={`border p-6 text-left transition-all duration-300 ${
                  path === "ap"
                    ? "border-[#8B5CF6] bg-[#8B5CF6]/15 shadow-[0_0_24px_rgba(139,92,246,0.25)]"
                    : "border-white/10 hover:border-[#8B5CF6]/50"
                }`}
              >
                <p className="font-display text-xl text-[#F0F2F8]">
                  I want experience
                </p>
                <p className="mt-2 text-sm text-[#8E95A5]">
                  The vibrational threshold and beyond-body exploration.
                </p>
                <p className="mt-4 font-mono-ui text-[10px] uppercase tracking-[0.2em] text-[#38BDF8]">
                  Astral Projection
                </p>
              </button>
            </div>
            <div
              data-testid="newcomer-result-panel"
              className="mt-8 border-t border-white/[0.07] pt-8"
            >
              {rec ? (
                <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="eyebrow !text-[10px]">Your first manual</p>
                    <p className="font-display mt-3 text-2xl text-[#F0F2F8]">
                      {rec.title}
                    </p>
                    <p className="mt-2 text-sm text-[#5F6677]">{rec.format}</p>
                  </div>
                  <div className="flex shrink-0 flex-wrap gap-3">
                    <Link
                      to="/guidebooks"
                      data-testid="newcomer-result-guidebooks-btn"
                      className="inline-flex items-center gap-2 border border-white/20 px-5 py-3 font-mono-ui text-[10px] uppercase tracking-[0.2em] text-[#F0F2F8] transition-all duration-300 hover:border-[#8B5CF6]"
                    >
                      All guidebooks
                    </Link>
                    <a
                      href={SHOP_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="newcomer-result-shop-btn"
                      className="inline-flex items-center gap-2 border border-[#8B5CF6] bg-[#8B5CF6]/15 px-5 py-3 font-mono-ui text-[10px] uppercase tracking-[0.2em] text-[#F0F2F8] transition-all duration-300 hover:bg-[#8B5CF6]/30"
                    >
                      Order on Fourthwall <ArrowUpRight className="h-3 w-3 text-[#A78BFA]" />
                    </a>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-[#5F6677]">
                  Select a doorway above to see your recommended first manual.
                </p>
              )}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function PortalCta() {
  return (
    <section className="relative overflow-hidden border-t border-white/[0.07]">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_75%_40%,rgba(139,92,246,0.22),transparent_32%),linear-gradient(120deg,#07080B_0%,#0D1020_58%,#120B24_100%)]" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#07080B] via-[#07080B]/70 to-[#07080B]/40" />
      <StarfieldCanvas className="absolute inset-0" interactive={false} density={0.00008} />
      <div className="relative mx-auto max-w-7xl px-5 py-28 sm:px-8 lg:px-10 lg:py-36">
        <Reveal>
          <p className="eyebrow">The Storefront</p>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-display mt-6 max-w-2xl text-3xl font-light leading-[1.1] tracking-tight sm:text-5xl">
            Every path begins with a manual.
          </h2>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="body-muted mt-6 max-w-lg text-sm leading-relaxed sm:text-base">
            All guidebooks, workbooks and codexes are sold through our official
            Fourthwall shop — secure checkout, worldwide dispatch, instant
            digital delivery.
          </p>
        </Reveal>
        <Reveal delay={0.3}>
          <a
            href={SHOP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="home-cta-shop-btn"
            className="mt-10 inline-flex items-center gap-3 border border-[#8B5CF6] bg-[#8B5CF6]/15 px-8 py-4 font-mono-ui text-[11px] uppercase tracking-[0.22em] text-[#F0F2F8] transition-all duration-300 hover:bg-[#8B5CF6]/30 hover:shadow-[0_0_32px_rgba(139,92,246,0.45)]"
          >
            Visit the Fourthwall Shop
            <ArrowUpRight className="h-4 w-4 text-[#A78BFA]" />
          </a>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  usePageMeta(
    "Consciousness Ascending — Your Own Remote Path",
    "Guidebooks and protocols for remote viewing and astral projection. Explore the disciplines, then visit our official Fourthwall shop."
  );
  return (
    <>
      <Hero />
      <EditorialRibbon items={RIBBON_ITEMS} />
      <Manifesto />
      <Pillars />
      <FeaturedGuidebooks />
      <Newcomer />
      <PortalCta />
    </>
  );
}
